import type { ColumnDef } from '@tanstack/react-table'
import { MoreHorizontal, RefreshCw, Search } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { trpcClient } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import { PageFrame } from 'shared/components/page-frame'
import { CompactTable, EmptyTableBody, SectionTitle, Stat, Stats } from 'shared/components/section'
import { ToneNote } from 'shared/components/tone-banner'
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from 'shared/components/ui/alert-dialog'
import { Badge } from 'shared/components/ui/badge'
import { Button } from 'shared/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from 'shared/components/ui/dialog'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from 'shared/components/ui/dropdown-menu'
import { Input } from 'shared/components/ui/input'
import { Label } from 'shared/components/ui/label'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from 'shared/components/ui/sheet'
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shared/components/ui/table'
import { useDataTable } from 'shared/hooks/use-data-table'
import { useTranslate } from 'shared/lib/i18n'
import { WHOLE } from 'shared/lib/money'
import { formatDateTime, formatDayTime } from 'shared/lib/utils/date'
import {
	type BindingCode,
	type NoticeLogRow,
	type NotificationTarget,
	type NotificationTargets,
	boundSince,
	countdown,
	hiddenPoints,
	shownTarget,
	isChatId,
	notificationFailureKey,
	secondsLeft,
} from './notifications-model'

/**
 * «Оповещения» — the admin's screen of staff notifications in Telegram (ADR-0030,
 * ticket 46).
 *
 * On top, whether the notices reach anyone now: outside prod nothing leaves for
 * Telegram (said once, plainly); the figures — groups bound, groups the bot was
 * removed from, Points without a group, notices not delivered. Below, every target a
 * group is bound to (support, the cashier, the hub, each Point) as one table — its
 * group, who bound it — with the one frequent act on the row («Привязать» or
 * «Проверить») and the rest in «⋯». Binding is a dialog: the code the group says to
 * the bot, with its countdown, the screen noticing by itself when the group is bound;
 * outside prod, where no bot listens, the chat id instead. Unbinding asks first. Last,
 * the log of the latest notices; a click on one opens its text and how each group's
 * delivery went.
 *
 * Every call is injected (`NotificationsCalls`) so the screen is tested without a
 * backend; the defaults are the real tRPC client and the wall clock.
 */

export interface NotificationsCalls {
	loadTargets: () => Promise<NotificationTargets>
	loadLog: () => Promise<NoticeLogRow[]>
	startBinding: (target: string) => Promise<BindingCode>
	unbind: (target: string) => Promise<unknown>
	test: (target: string) => Promise<unknown>
	bindManually: (input: { target: string; chatId: string; chatTitle: string }) => Promise<unknown>
	now: () => number
	/** How often the screen looks whether a pending binding has happened, ms. */
	pollMs: number
}

const trpcCalls: NotificationsCalls = {
	loadTargets: () => trpcClient.notifications.Targets.query({}),
	loadLog: () => trpcClient.notifications.Log.query({}),
	startBinding: (target) => trpcClient.notifications.StartBinding.mutate({ target }),
	unbind: (target) => trpcClient.notifications.Unbind.mutate({ target }),
	test: (target) => trpcClient.notifications.Test.mutate({ target }),
	bindManually: (input) => trpcClient.notifications.BindManually.mutate(input),
	now: () => Date.now(),
	pollMs: 3_000,
}

/** A binding in progress: its code, and when it was asked for. */
interface Pending {
	target: string
	code: BindingCode
	startedAt: number
}

/** What the last act answered, and about which target (none: the screen as a whole). */
interface Message {
	target: string | null
	text: string
	tone: 'ok' | 'error'
}

/** A target's id as it goes into a test id: `location:p-101` → `location-p-101`. */
const slug = (target: string) => target.replace(/[^a-z0-9]+/gi, '-')

export function Notifications(props: Partial<NotificationsCalls>) {
	const calls: NotificationsCalls = { ...trpcCalls, ...props }
	const { t } = useTranslate()
	const [targets, setTargets] = useState<NotificationTargets | null>(null)
	const [log, setLog] = useState<NoticeLogRow[] | null>(null)
	const [pending, setPending] = useState<Pending | null>(null)
	const [message, setMessage] = useState<Message | null>(null)
	const [filter, setFilter] = useState('')
	const [allPoints, setAllPoints] = useState(false)
	const [unbinding, setUnbinding] = useState<NotificationTarget | null>(null)
	const [openNotice, setOpenNotice] = useState<string | null>(null)
	const [busy, setBusy] = useState(false)
	const { loadTargets, loadLog } = calls

	const refresh = useCallback(() => {
		loadTargets().then(setTargets, (e) => setMessage({ target: null, text: String(e), tone: 'error' }))
		loadLog().then(setLog, (e) => setMessage({ target: null, text: String(e), tone: 'error' }))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(refresh, [refresh])

	const run = async (target: string | null, action: () => Promise<string | null>) => {
		setBusy(true)
		setMessage(null)
		try {
			const ok = await action()
			if (ok) setMessage({ target, text: ok, tone: 'ok' })
		} catch (e) {
			setMessage({ target, text: t(notificationFailureKey(e)), tone: 'error' })
		} finally {
			refresh()
			setBusy(false)
		}
	}

	// Only this target's binding is closed — never one the admin started since for another.
	const closeBinding = (target: string) => setPending((p) => (p?.target === target ? null : p))

	const startBinding = (target: string) =>
		run(target, async () => {
			const code = await calls.startBinding(target)
			setPending({ target, code, startedAt: calls.now() })
			return null
		})

	const test = (target: string) =>
		run(target, async () => {
			await calls.test(target)
			return t('notifications.testQueued')
		})

	const unbind = (target: string) =>
		run(target, async () => {
			await calls.unbind(target)
			return t('notifications.unbound')
		})

	const all = useMemo(() => targets?.targets ?? [], [targets])
	const rows = useMemo(
		() => all.filter((target) => shownTarget(target, filter, allPoints || pending?.target === target.target)),
		[all, filter, allPoints, pending],
	)
	const pendingTarget = pending ? (all.find((x) => x.target === pending.target) ?? null) : null
	const notice = log?.find((n) => n.noticeId === openNotice) ?? null

	const bound = all.filter((x) => x.group?.state === 'active').length
	const removed = all.filter((x) => x.group?.state === 'bot_removed').length
	const pointsWithout = all.filter((x) => x.kind === 'point' && !x.group).length
	const undelivered = log?.filter((n) => n.status === 'undelivered').length ?? 0

	const columns: ColumnDef<NotificationTarget>[] = [
		{
			id: 'name',
			header: t('notifications.colTarget'),
			cell: ({ row: { original: target } }) => (
				<span
					className={target.kind === 'point' ? 'font-mono font-semibold' : 'font-medium'}
					data-testid={`notification-target-${slug(target.target)}-name`}
				>
					{targetName(t, target)}
				</span>
			),
		},
		{
			id: 'group',
			header: t('notifications.colGroup'),
			cell: ({ row: { original: target } }) => {
				const id = slug(target.target)
				const { group } = target
				return (
					<span className='flex items-center gap-2' data-testid={`notification-target-${id}-group`}>
						{group ? (
							<>
								{group.chatTitle}
								{group.state === 'bot_removed' && (
									<Badge variant='destructive' data-testid={`notification-target-${id}-removed`}>
										{t('notifications.botRemoved')}
									</Badge>
								)}
							</>
						) : (
							<span className='text-muted-foreground'>{t('notifications.noGroup')}</span>
						)}
					</span>
				)
			},
		},
		{
			id: 'boundBy',
			header: t('notifications.colBoundBy'),
			cell: ({ row: { original: target } }) =>
				target.group ? (
					<span className='text-muted-foreground'>
						{t('notifications.boundByShort', { name: target.group.boundByName, at: formatDayTime(target.group.boundAt) })}
					</span>
				) : null,
		},
		{
			id: 'actions',
			header: '',
			meta: { cellClassName: 'w-px text-right' },
			cell: ({ row: { original: target } }) => {
				const id = slug(target.target)
				const { group } = target
				const active = group?.state === 'active'
				return (
					<div className='flex items-center justify-end gap-1'>
						{active ? (
							<Button
								size='sm'
								variant='outline'
								disabled={busy}
								onClick={() => test(target.target)}
								data-testid={`notification-target-${id}-test`}
							>
								{t('notifications.test')}
							</Button>
						) : (
							<Button
								size='sm'
								variant='outline'
								disabled={busy}
								onClick={() => startBinding(target.target)}
								data-testid={`notification-target-${id}-bind`}
							>
								{group ? t('notifications.rebind') : t('notifications.bind')}
							</Button>
						)}
						{group ? (
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button
										variant='ghost'
										size='icon-sm'
										disabled={busy}
										aria-label={t('notifications.more')}
										data-testid={`notification-target-${id}-menu`}
									>
										<MoreHorizontal />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align='end'>
									{active && (
										<DropdownMenuItem
											onSelect={() => startBinding(target.target)}
											data-testid={`notification-target-${id}-bind`}
										>
											{t('notifications.rebind')}
										</DropdownMenuItem>
									)}
									<DropdownMenuItem
										variant='destructive'
										onSelect={() => setUnbinding(target)}
										data-testid={`notification-target-${id}-unbind`}
									>
										{t('notifications.unbind')}
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
						) : (
							// The «⋯» column keeps its width on a row with nothing more to do.
							<span className='inline-block size-8' />
						)}
					</div>
				)
			},
		},
	]

	const { table } = useDataTable({
		data: rows,
		columns,
		rowCount: rows.length,
		...WHOLE,
		// The rows are filtered here: a new list must not reset the page in a loop.
		autoResetPageIndex: false,
		getRowId: (x) => x.target,
	})

	const hidden = targets && !filter.trim() ? hiddenPoints(all, filter, false).length : 0

	return (
		<PageFrame title='notifications.title'>
			<div className='flex flex-col gap-6' data-testid='notifications-screen'>
				{targets && !targets.live && (
					<ToneNote
						tone='attention'
						title={t('notifications.notLive')}
						detail={t('notifications.notLiveDetail')}
						testId='notifications-mode'
					/>
				)}

				{targets && (
					<Stats testId='notifications-stats'>
						{targets.live && (
							<Stat
								label={t('notifications.statBot')}
								value={targets.botUsername ? `@${targets.botUsername}` : '—'}
								testId='notifications-mode'
							/>
						)}
						<Stat label={t('notifications.statBound')} value={bound} />
						<Stat label={t('notifications.statBotRemoved')} value={removed} tone='bad' />
						<Stat label={t('notifications.statNoGroup')} value={pointsWithout} tone='warn' />
						<Stat label={t('notifications.statUndelivered')} value={undelivered} tone='bad' />
					</Stats>
				)}

				{message && (
					<ToneNote
						tone={message.tone === 'ok' ? 'ready' : 'refused'}
						title={message.text}
						testId={
							message.target === null ? 'notifications-message' : `notification-target-${slug(message.target)}-message`
						}
					/>
				)}

				<section className='flex flex-col gap-2'>
					<SectionTitle
						title={t('notifications.targetsTitle')}
						count={rows.length}
						hint={t('notifications.intro')}
						aside={
							<>
								{targets && !filter.trim() && (hidden > 0 || allPoints) && (
									<Button
										variant='ghost'
										size='sm'
										onClick={() => setAllPoints(!allPoints)}
										data-testid='notifications-all-points'
									>
										{allPoints
											? t('notifications.hideUnbound')
											: t('notifications.showUnbound', { count: String(hidden) })}
									</Button>
								)}
								<div className='relative w-64'>
									<Search className='pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground' />
									<Input
										className='h-8 pl-8'
										placeholder={t('notifications.filter')}
										aria-label={t('notifications.filter')}
										value={filter}
										onChange={(e) => setFilter(e.target.value)}
										data-testid='notifications-filter'
									/>
								</div>
							</>
						}
					/>
					<DataTable
						table={table}
						withPagination={false}
						maxHeightClassName='max-h-[28rem]'
						emptyText={t('notifications.targetsEmpty')}
						row={{ attributes: (x) => ({ 'data-testid': `notification-target-${slug(x.target)}` }) }}
					/>
				</section>

				<NoticeLog log={log} onRefresh={refresh} onOpen={setOpenNotice} />
			</div>

			{pending && pendingTarget && (
				<BindingDialog
					key={`${pending.target}:${pending.code.code}`}
					target={pendingTarget}
					pending={pending}
					manualBinding={targets?.manualBinding ?? false}
					busy={busy}
					calls={calls}
					onCancel={() => closeBinding(pending.target)}
					onBound={() => {
						closeBinding(pending.target)
						setMessage({
							target: pending.target,
							text: t('notifications.boundNow', { chat: pendingTarget.group?.chatTitle ?? '' }),
							tone: 'ok',
						})
						loadLog().then(setLog, () => undefined)
					}}
					onPoll={() => loadTargets().then(setTargets, () => undefined)}
					onRestart={() => startBinding(pending.target)}
					onBindManually={(chatId, chatTitle) =>
						run(pending.target, async () => {
							await calls.bindManually({ target: pending.target, chatId, chatTitle })
							closeBinding(pending.target)
							return t('notifications.boundNow', { chat: chatTitle || chatId })
						})
					}
				/>
			)}

			<AlertDialog open={unbinding !== null} onOpenChange={(o) => !o && setUnbinding(null)}>
				<AlertDialogContent data-testid='notifications-unbind-dialog'>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t('notifications.unbindAsk', { chat: unbinding?.group?.chatTitle ?? '' })}
						</AlertDialogTitle>
						<AlertDialogDescription>{t('notifications.unbindHint')}</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t('notifications.cancel')}</AlertDialogCancel>
						<Button
							variant='destructive'
							disabled={busy}
							onClick={() => {
								const target = unbinding!.target
								setUnbinding(null)
								void unbind(target)
							}}
							data-testid='notifications-unbind-confirm'
						>
							{t('notifications.unbind')}
						</Button>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<Sheet open={notice !== null} onOpenChange={(o) => !o && setOpenNotice(null)}>
				<SheetContent className='overflow-y-auto data-[side=right]:sm:max-w-xl'>
					{notice && <NoticeSheet row={notice} />}
				</SheetContent>
			</Sheet>
		</PageFrame>
	)
}

function targetName(t: ReturnType<typeof useTranslate>['t'], target: NotificationTarget): string {
	if (target.kind === 'role') return t(target.role === 'support' ? 'notifications.roleSupport' : 'notifications.roleCashier')
	return target.locationCode ?? ''
}

/** A binding in progress, in a dialog: the code and its countdown, or — outside prod — the chat id. */
function BindingDialog(props: {
	target: NotificationTarget
	pending: Pending
	manualBinding: boolean
	busy: boolean
	calls: NotificationsCalls
	onCancel: () => void
	onBound: () => void
	onPoll: () => void
	onRestart: () => void
	onBindManually: (chatId: string, chatTitle: string) => void
}) {
	const { t } = useTranslate()
	const { pending, calls, onPoll, onBound } = props
	const id = slug(props.target.target)
	const [now, setNow] = useState(calls.now())
	const [chatId, setChatId] = useState('')
	const [chatTitle, setChatTitle] = useState('')
	const left = secondsLeft(pending.code.expiresAt, now)
	const bound = boundSince(props.target.group, pending.startedAt)

	// The countdown ticks; the screen looks whether the group said the code.
	useEffect(() => {
		const tick = setInterval(() => setNow(calls.now()), 1_000)
		const poll = setInterval(onPoll, calls.pollMs)
		return () => {
			clearInterval(tick)
			clearInterval(poll)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (bound) onBound()
	}, [bound, onBound])

	return (
		<Dialog open onOpenChange={(o) => !o && props.onCancel()}>
			<DialogContent className='sm:max-w-lg' data-testid={`notification-target-${id}-binding`}>
				<DialogHeader>
					<DialogTitle>{t('notifications.bindTitle', { target: targetName(t, props.target) })}</DialogTitle>
					<DialogDescription>{t('notifications.bindHow')}</DialogDescription>
				</DialogHeader>
				{left > 0 ? (
					<div className='flex flex-col gap-2'>
						<code
							className='w-fit rounded-md bg-muted px-3 py-2 font-mono text-base select-all'
							data-testid={`notification-target-${id}-command`}
						>
							{pending.code.command}
						</code>
						<p className='text-sm text-muted-foreground' data-testid={`notification-target-${id}-countdown`}>
							{t('notifications.codeLeft', { left: countdown(left) })}
						</p>
					</div>
				) : (
					<div className='flex items-center gap-3' data-testid={`notification-target-${id}-expired`}>
						<span className='text-sm text-destructive'>{t('notifications.codeExpired')}</span>
						<Button size='sm' variant='outline' onClick={props.onRestart}>
							{t('notifications.newCode')}
						</Button>
					</div>
				)}
				{props.manualBinding && (
					<div className='flex flex-col gap-3 border-t pt-4'>
						<p className='text-sm text-muted-foreground'>{t('notifications.manualHint')}</p>
						<div className='grid grid-cols-2 gap-3'>
							<div className='grid gap-1.5'>
								<Label htmlFor={`chat-id-${id}`}>{t('notifications.chatIdLabel')}</Label>
								<Input
									id={`chat-id-${id}`}
									className='font-mono'
									placeholder='-1001234567890'
									value={chatId}
									onChange={(e) => setChatId(e.target.value)}
									data-testid={`notification-target-${id}-chat-id`}
								/>
							</div>
							<div className='grid gap-1.5'>
								<Label htmlFor={`chat-title-${id}`}>{t('notifications.chatTitle')}</Label>
								<Input
									id={`chat-title-${id}`}
									value={chatTitle}
									onChange={(e) => setChatTitle(e.target.value)}
									data-testid={`notification-target-${id}-chat-title`}
								/>
							</div>
						</div>
					</div>
				)}
				<DialogFooter>
					<Button variant='outline' onClick={props.onCancel}>
						{t('notifications.cancel')}
					</Button>
					{props.manualBinding && (
						<Button
							disabled={props.busy || !isChatId(chatId)}
							onClick={() => props.onBindManually(chatId.trim(), chatTitle.trim())}
							data-testid={`notification-target-${id}-bind-manually`}
						>
							{t('notifications.bindManually')}
						</Button>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

const STATUS_VARIANT: Record<NoticeLogRow['status'], 'default' | 'secondary' | 'destructive' | 'outline'> = {
	sent: 'secondary',
	undelivered: 'destructive',
	waiting: 'outline',
	retrying: 'outline',
	not_sent_not_prod: 'outline',
	no_group: 'outline',
}

/** The latest notices, newest first; a click opens one. */
function NoticeLog({
	log,
	onRefresh,
	onOpen,
}: {
	log: NoticeLogRow[] | null
	onRefresh: () => void
	onOpen: (noticeId: string) => void
}) {
	const { t } = useTranslate()
	const rows = useMemo(() => log ?? [], [log])

	const columns: ColumnDef<NoticeLogRow>[] = [
		{
			id: 'at',
			header: t('notifications.colAt'),
			meta: { cellClassName: 'whitespace-nowrap text-muted-foreground tabular-nums' },
			cell: ({ row }) => formatDayTime(row.original.createdAt),
		},
		{
			id: 'kind',
			header: t('notifications.colKind'),
			cell: ({ row }) => (
				<span className='font-medium' data-testid={`notice-${row.original.noticeId}-kind`}>
					{t(`notificationKind.${row.original.kind}`)}
				</span>
			),
		},
		{ id: 'place', header: t('notifications.colPlace'), cell: ({ row }) => row.original.placeLabel },
		{
			id: 'targets',
			header: t('notifications.colTargets'),
			cell: ({ row }) => (
				<span className='text-muted-foreground' data-testid={`notice-${row.original.noticeId}-targets`}>
					{row.original.targets.join(', ')}
				</span>
			),
		},
		{
			id: 'status',
			header: t('notifications.colStatus'),
			cell: ({ row }) => (
				<Badge variant={STATUS_VARIANT[row.original.status]} data-testid={`notice-${row.original.noticeId}-status`}>
					{t(`notificationStatus.${row.original.status}`)}
				</Badge>
			),
		},
	]

	const { table } = useDataTable({
		data: rows,
		columns,
		rowCount: rows.length,
		...WHOLE,
		getRowId: (n) => n.noticeId,
	})

	return (
		<section className='flex flex-col gap-2'>
			<SectionTitle
				title={t('notifications.logTitle')}
				count={log?.length}
				hint={t('notifications.logHint')}
				aside={
					<Button variant='ghost' size='sm' onClick={onRefresh} data-testid='notifications-refresh'>
						<RefreshCw />
						{t('nav.refresh')}
					</Button>
				}
			/>
			<div data-testid={log && log.length === 0 ? 'notifications-log-empty' : 'notifications-log'}>
				<DataTable
					table={table}
					withPagination={false}
					emptyText={t('notifications.logEmpty')}
					row={{
						attributes: (n) => ({ 'data-testid': `notice-${n.noticeId}` }),
						onRowClick: (n) => onOpen(n.noticeId),
					}}
				/>
			</div>
		</section>
	)
}

/** One notice: what it said, to whom, and how each group's delivery went. */
function NoticeSheet({ row }: { row: NoticeLogRow }) {
	const { t } = useTranslate()
	return (
		<div className='flex flex-col gap-6 pb-6' data-testid={`notice-${row.noticeId}-sheet`}>
			<SheetHeader className='pr-14 pb-0'>
				<SheetTitle className='flex items-center gap-2 text-lg'>
					{t(`notificationKind.${row.kind}`)}
					<Badge variant={STATUS_VARIANT[row.status]}>{t(`notificationStatus.${row.status}`)}</Badge>
				</SheetTitle>
				<SheetDescription>
					{formatDateTime(row.createdAt)}
					{row.placeLabel ? ` · ${row.placeLabel}` : ''}
				</SheetDescription>
			</SheetHeader>
			<div className='flex flex-col gap-6 px-4'>
				<div className='flex flex-col gap-1'>
					<span className='text-xs text-muted-foreground'>{t('notifications.noticeText')}</span>
					<p className='rounded-md bg-muted p-3 text-sm whitespace-pre-line' data-testid={`notice-${row.noticeId}-text`}>
						{row.parts.join('\n\n')}
					</p>
				</div>
				<section className='flex flex-col gap-2'>
					<SectionTitle title={t('notifications.deliveries')} count={row.deliveries.length} />
					<CompactTable>
						<TableHeader>
							<TableRow>
								<TableHead>{t('notifications.colGroup')}</TableHead>
								<TableHead>{t('notifications.colStatus')}</TableHead>
								<TableHead className='text-right'>{t('notifications.colAttempts')}</TableHead>
							</TableRow>
						</TableHeader>
						{row.deliveries.length === 0 ? (
							<EmptyTableBody columns={3}>{t('notifications.noDeliveries')}</EmptyTableBody>
						) : (
							<TableBody>
								{row.deliveries.map((d) => (
									<TableRow key={d.chatId}>
										<TableCell className='whitespace-normal'>
											<div>{d.chatTitle}</div>
											{d.lastError && <div className='text-xs text-destructive'>{d.lastError}</div>}
										</TableCell>
										<TableCell>
											<Badge variant={STATUS_VARIANT[d.status as NoticeLogRow['status']] ?? 'outline'}>
												{t(`notificationStatus.${d.status}` as never)}
											</Badge>
										</TableCell>
										<TableCell className='text-right tabular-nums'>{d.attempts}</TableCell>
									</TableRow>
								))}
							</TableBody>
						)}
					</CompactTable>
				</section>
			</div>
		</div>
	)
}
